import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Modal, Spinner } from 'react-bootstrap'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Row,
  UncontrolledTooltip,
} from 'reactstrap'
import Select from 'react-select'
import * as XLSX from 'xlsx'

import auth from '../../auth/service'
import Header from '../../components/Headers/Header'
import FileInput from '../../components/molecules/FileInput'
import { notify } from '../../utils/notify'
import { ToastContainer } from 'react-toastify'
import { api } from '../../utils'
import { useFileUpload } from '../../hooks'
import SetTitle from '../../utils/set_title'
import { DEFAULT_QUESTION_PROMPT } from './defaultPrompt'
import ClienteField from './ClienteField'
import './LLMVisibilityScheduledWizardForm.css'

const today = new Date().toISOString().slice(0, 10)
const keywordTemplateUrl = `${process.env.PUBLIC_URL}/templates/llm_visibility_keyword_template.xlsx`

const createDraftItem = () => ({
  id: null,
  name: '',
  display_name: '',
  description: '',
  synonymsText: '',
})

const listConfigs = {
  competitors: {
    label: 'Competitor',
    pluralLabel: 'Competitor',
    endpoint: 'competitors',
    description: 'Brand e domini concorrenti usati nelle analisi di visibilita.',
    hasSynonyms: true,
  },
  personas: {
    label: 'Persona',
    pluralLabel: 'Personas',
    endpoint: 'personas',
    description: 'Profili utente da usare nella generazione e lettura delle risposte.',
    hasDescription: true,
  },
  topics: {
    label: 'Topic',
    pluralLabel: 'Topic',
    endpoint: 'topics',
    description: 'Categorie usate per raggruppare e leggere le risposte generate.',
    hasDescription: true,
  },
}

const steps = [
  { key: 'base', label: 'Base' },
  { key: 'competitors', label: 'Competitor' },
  { key: 'personas', label: 'Personas' },
  { key: 'topics', label: 'Topic' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'llm', label: 'LLM' },
  { key: 'prompt', label: 'Prompt' },
]

const slugifyName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')

const toBigQuerySlug = (value) =>
  slugifyName(value)
    .replace(/-/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')

const normalizeBrandAliases = (value) => {
  const seen = new Set()

  return String(value || '')
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item) => {
      const normalized = item.toLowerCase()
      if (seen.has(normalized)) {
        return false
      }
      seen.add(normalized)
      return true
    })
}

const normalizeListItem = (item) => ({
  ...item,
  name: item.name || slugifyName(item.display_name),
  display_name: item.display_name || item.name,
})

const toDraftItem = (item = {}) => ({
  id: item.id || null,
  name: item.name || '',
  display_name: item.display_name || '',
  description: item.description || '',
  synonymsText: Array.isArray(item.synonyms) ? item.synonyms.join(', ') : '',
})

const listSheetAliases = {
  competitors: ['competitors', 'competitor'],
  personas: ['personas', 'persona'],
  topics: ['topics', 'topic'],
}

const normalizeImportKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

const getImportedValue = (row, keys) => {
  const normalizedKeys = keys.map(normalizeImportKey)
  const foundKey = Object.keys(row).find((key) => normalizedKeys.includes(normalizeImportKey(key)))
  return foundKey ? String(row[foundKey] || '').trim() : ''
}

// Accept multiple header labels so existing keyword templates can evolve without breaking imports.
const parseRelatedListSheet = (worksheet, kind) => {
  const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' })

  return rows
    .map((row) => {
      const explicitDisplayName = getImportedValue(row, [
        'display_name',
        'display name',
        'nome visibile',
        'label',
        'title',
      ])
      const fallbackName = getImportedValue(row, ['name', 'nome'])
      const displayName = explicitDisplayName || fallbackName
      const technicalName = getImportedValue(row, [
        'technical_name',
        'technical name',
        'nome tecnico',
        'slug',
      ])
      const description = getImportedValue(row, ['description', 'descrizione'])
      const synonymsText = getImportedValue(row, [
        'synonyms',
        'synonym',
        'sinonimi',
        'aliases',
        'alias',
      ])

      return normalizeListItem({
        id: null,
        name: technicalName,
        display_name: displayName,
        description: kind === 'personas' || kind === 'topics' ? description : '',
        synonymsText: kind === 'competitors' ? synonymsText : '',
      })
    })
    .filter((item) => item.name || item.display_name || item.description || item.synonymsText)
}

const parseRelatedListsFromWorkbook = (workbook) => {
  const normalizedSheetNames = workbook.SheetNames.reduce(
    (sheetMap, sheetName) => ({ ...sheetMap, [normalizeImportKey(sheetName)]: sheetName }),
    {}
  )

  return Object.keys(listSheetAliases).reduce((relatedLists, kind) => {
    const sheetName = listSheetAliases[kind]
      .map(normalizeImportKey)
      .map((alias) => normalizedSheetNames[alias])
      .find(Boolean)

    if (!sheetName) {
      return relatedLists
    }

    return {
      ...relatedLists,
      [kind]: parseRelatedListSheet(workbook.Sheets[sheetName], kind),
    }
  }, {})
}

const readWorkbookFromFile = (selectedFile) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        resolve(XLSX.read(event.target.result, { type: 'array' }))
      } catch (error) {
        reject(error)
      }
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(selectedFile)
  })

const toPayload = (kind, item) => {
  const normalized = normalizeListItem(item)
  const payload = {
    name: normalized.name,
    display_name: normalized.display_name,
  }

  if (kind === 'competitors') {
    payload.synonyms = normalized.synonymsText
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  }

  if (kind === 'personas' || kind === 'topics') {
    payload.description = normalized.description || ''
  }

  return payload
}

const getItemLabel = (item) => item.display_name || item.name || 'Elemento senza nome'

const formatCost = (value) => `${Number(value || 0).toFixed(2)}\u20AC`

const getRunsPerYear = (scheduleType, interval) => {
  const normalizedInterval = Math.max(1, Number(interval) || 1)
  return scheduleType === 'W' ? 52 / normalizedInterval : 12 / normalizedInterval
}

const getMonthlyRunFactor = (scheduleType, interval) => getRunsPerYear(scheduleType, interval) / 12
const hasAction = (actions, action) => (actions || []).includes(action)
const actionLabels = {
  SAVE_CONFIG: 'Salva configurazione',
  EXPORT_CONFIG: 'Esporta configurazione',
  PAUSE_SCHEDULE: 'Pausa schedule',
  RESUME_SCHEDULE: 'Riattiva schedule',
  GENERATE_QUESTIONS: 'Genera domande',
  REGENERATE_QUESTIONS: 'Rigenera domande',
  START_SCHEDULED_ANSWERS: 'Avvia risposte schedulate',
  START_FULL_ANALYSIS: 'Esegui analisi completa',
  TAG_QUESTIONS: 'Tagga domande',
  MANAGE_TAGGING: 'Gestisci tagging',
  RESUME_FAILED_NODE: 'Riprendi nodo fallito',
  RERUN_EXECUTION: 'Rilancia esecuzione',
  RERUN_BRAND_EXTRACTION: 'Rilancia brand extraction',
  RERUN_TOPIC_EXTRACTION: 'Rilancia topic extraction',
  REFRESH_FINAL_VIEWS: 'Aggiorna viste finali',
  DELETE_REPORT: 'Elimina report',
  WAIT: 'Attendi completamento',
}

const actionMessages = {
  PAUSE_SCHEDULE: 'Schedule messa in pausa.',
  RESUME_SCHEDULE: 'Schedule riattivata.',
  GENERATE_QUESTIONS: 'Generazione domande avviata.',
  REGENERATE_QUESTIONS: 'Rigenerazione domande avviata.',
  START_SCHEDULED_ANSWERS: 'Avvio risposte schedulate richiesto.',
}
const actionTooltips = {
  SAVE_CONFIG: 'Salva le modifiche alla configurazione senza generare domande e senza avviare run.',
  EXPORT_CONFIG:
    'Scarica un file Excel con keyword, competitor, personas e topic della configurazione.',
  PAUSE_SCHEDULE:
    'Mette in pausa la schedule: non verranno pianificate nuove esecuzioni automatiche.',
  RESUME_SCHEDULE: 'Riattiva una schedule in pausa, mantenendo la configurazione corrente.',
  GENERATE_QUESTIONS:
    'Avvia la generazione delle domande per una schedule in modalita questions only.',
  REGENERATE_QUESTIONS: 'Rigenera le domande gia create, sostituendo il set precedente.',
  START_SCHEDULED_ANSWERS:
    'Avvia esplicitamente il run delle risposte schedulate usando la configurazione salvata.',
  START_FULL_ANALYSIS:
    'Continua un report questions only ed esegue la pipeline completa di risposte e analisi.',
  TAG_QUESTIONS: 'Crea o collega il tagging necessario per classificare le domande generate.',
  MANAGE_TAGGING: 'Apre il flusso di gestione del tagging collegato al report.',
  RESUME_FAILED_NODE:
    'Riprende un report in errore dal nodo della pipeline che puo essere recuperato.',
  RERUN_EXECUTION:
    'Rilancia una singola esecuzione schedulata quando il report finale non e ancora pronto.',
  RERUN_BRAND_EXTRACTION:
    'Ricalcola le menzioni brand sulle risposte gia generate senza rieseguire il report principale.',
  RERUN_TOPIC_EXTRACTION:
    'Ricalcola i topic sulle risposte gia generate senza rieseguire il report principale.',
  REFRESH_FINAL_VIEWS:
    'Aggiorna le viste finali derivate, utile dopo modifiche a tagging o post-processing.',
  DELETE_REPORT:
    'Elimina il report dalla vista operativa mantenendo la cancellazione gestita dal backend.',
  WAIT: 'Nessuna azione manuale disponibile mentre il processo corrente e in corso.',
}

const actionButtonColors = {
  SAVE_CONFIG: 'info',
  PAUSE_SCHEDULE: 'secondary',
  RESUME_SCHEDULE: 'success',
  GENERATE_QUESTIONS: 'info',
  REGENERATE_QUESTIONS: 'info',
  START_SCHEDULED_ANSWERS: 'success',
}
const actionButtonClasses = {
  SAVE_CONFIG: 'llm-wizard-action-btn--save',
  EXPORT_CONFIG: 'llm-wizard-action-btn--export',
  REGENERATE_QUESTIONS: 'llm-wizard-action-btn--regenerate',
  START_SCHEDULED_ANSWERS: 'llm-wizard-action-btn--start-answers',
}

const ScheduleEstimateModal = ({ show, onHide, onSubmit, params, scheduleType, interval }) => {
  const estimate = Number(params.estimate || 0)
  const setupEstimate = Number(params.setup_estimate || 0)
  const firstRunEstimate = Number(params.first_run_estimate || 0)
  const monthlyEstimate = estimate * getMonthlyRunFactor(scheduleType, interval)
  const annualEstimate = estimate * getRunsPerYear(scheduleType, interval)

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header>
        <Modal.Title className="d-flex justify-content-center align-items-center w-100">
          Stima dei costi schedulati
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="llm-wizard-estimate-modal">
        <p>
          Keyword trovate nel file: {params.n_keywords}
          <br />
          Tokens calcolati: circa {params.total_tokens}
        </p>
        <div className="llm-wizard-estimate-grid">
          <div>
            <span>Costo iniziale setup</span>
            <strong>{formatCost(setupEstimate)}</strong>
          </div>
          <div>
            <span>Costo primo run</span>
            <strong>{formatCost(firstRunEstimate)}</strong>
          </div>
          <div>
            <span>Costo per run</span>
            <strong>{formatCost(estimate)}</strong>
          </div>
          <div>
            <span>Costo mensile stimato</span>
            <strong>{formatCost(monthlyEstimate)}</strong>
          </div>
          <div>
            <span>Costo annuale stimato</span>
            <strong>{formatCost(annualEstimate)}</strong>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-center align-items-center">
        <Button color="primary" onClick={onSubmit}>
          Ok
        </Button>
        <Button color="danger" onClick={onHide}>
          Chiudi
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const LLMVisibilityScheduledWizardForm = () => {
  SetTitle('Scheduled LLM Visibility V2')
  const isWebranking = auth.userGroup === 'Webranking'
  const { id } = useParams()
  const history = useHistory()
  const isEdit = !!id
  const { upload, loading: uploadLoading } = useFileUpload()

  const [initialLoading, setInitialLoading] = useState(true)
  const [listsLoading, setListsLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showEstimateCheck, setShowEstimateCheck] = useState(false)
  const [estimateParams, setEstimateParams] = useState({
    n_keywords: -1,
    total_tokens: -1,
    estimate: -1,
    setup_estimate: -1,
    first_run_estimate: -1,
    phase_estimates: {},
  })
  const [estimateAccepted, setEstimateAccepted] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [visitedSteps, setVisitedSteps] = useState([0])

  const [name, setName] = useState('')
  const [selectedClientId, setSelectedClientId] = useState('')
  const [clienti, setClienti] = useState([])
  const [clientPropertyId, setClientPropertyId] = useState('')
  const [clientDomain, setClientDomain] = useState('')
  const [brandAliasesText, setBrandAliasesText] = useState('')
  const [country, setCountry] = useState('')
  const [file, setFile] = useState(null)
  const [useGpt, setUseGpt] = useState(false)
  const [useGemini, setUseGemini] = useState(false)
  const [usePerplexity, setUsePerplexity] = useState(false)
  const [useDataforseo, setUseDataforseo] = useState(false)
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_QUESTION_PROMPT)
  const [startDate, setStartDate] = useState(today)
  const [scheduleType, setScheduleType] = useState('M')
  const [interval, setIntervalValue] = useState(1)
  const [runMode, setRunMode] = useState('RECURRING')
  const [questionsOnly, setQuestionsOnly] = useState(false)
  const [questionsGenerated, setQuestionsGenerated] = useState(false)
  const [active, setActive] = useState(true)
  const [taggingToolId, setTaggingToolId] = useState('')
  const [taggingToolReports, setTaggingToolReports] = useState([])
  const [availableActions, setAvailableActions] = useState([])
  const [actionLoading, setActionLoading] = useState('')

  const [competitors, setCompetitors] = useState([])
  const [personas, setPersonas] = useState([])
  const [topics, setTopics] = useState([])
  const [originalCompetitors, setOriginalCompetitors] = useState([])
  const [originalPersonas, setOriginalPersonas] = useState([])
  const [originalTopics, setOriginalTopics] = useState([])

  const derivedClientPropertyName = name.trim()
  const derivedClientPropertySlug = toBigQuerySlug(name)
  const normalizedBrandAliases = normalizeBrandAliases(brandAliasesText)

  const listState = {
    competitors: { items: competitors, setItems: setCompetitors, original: originalCompetitors },
    personas: { items: personas, setItems: setPersonas, original: originalPersonas },
    topics: { items: topics, setItems: setTopics, original: originalTopics },
  }

  const applyRelatedListPrefill = (relatedLists) => {
    const importedKinds = Object.keys(relatedLists).filter((kind) => relatedLists[kind].length > 0)

    importedKinds.forEach((kind) => {
      listState[kind].setItems(relatedLists[kind])
    })

    return importedKinds
  }

  const handleKeywordFileChange = async (selectedFile) => {
    setFile(selectedFile)

    if (!selectedFile) {
      return
    }

    try {
      const workbook = await readWorkbookFromFile(selectedFile)
      const relatedLists = parseRelatedListsFromWorkbook(workbook)
      const importedKinds = applyRelatedListPrefill(relatedLists)

      if (importedKinds.length > 0) {
        notify(
          `Liste importate dal file: ${importedKinds
            .map((kind) => listConfigs[kind].pluralLabel)
            .join(', ')}.`,
          'success'
        )
      }
    } catch {
      notify(
        'Impossibile leggere le liste dal file Excel. Il file verra caricato comunque.',
        'warning'
      )
    }
  }

  const loadDetails = async () => {
    if (!id) {
      return null
    }
    const res = await api.get(`llm-visibility-schedules/${id}/`)
    const data = res.data
    setName(data.name || '')
    setSelectedClientId(data?.cliente?.id ? String(data.cliente.id) : '')
    setClientPropertyId(data?.client_property?.id ? String(data.client_property.id) : '')
    setClientDomain(data?.client_property?.client_domain || '')
    setBrandAliasesText(
      Array.isArray(data?.client_property?.brand_aliases)
        ? data.client_property.brand_aliases.join('\n')
        : ''
    )
    setCountry(data.country || '')
    setUseGpt(!!data.gpt)
    setUseGemini(!!data.gemini)
    setUsePerplexity(!!data.perplexity)
    setUseDataforseo(!!data.use_dataforseo)
    setCustomPrompt(data.custom_prompt || DEFAULT_QUESTION_PROMPT)
    setStartDate(data.start_date || today)
    setScheduleType(data.schedule_type || 'M')
    setIntervalValue(data.interval || 1)
    setRunMode(data.run_mode || 'RECURRING')
    setAvailableActions(data.available_actions || [])
    setQuestionsOnly(!!data.questions_only)
    setQuestionsGenerated(!!data.questions_generated)
    setActive(!!data.active)
    setTaggingToolId(data.connected_tagging || '')
    return data?.client_property?.id ? String(data.client_property.id) : ''
  }

  const loadRelatedLists = async (propertyId) => {
    if (!propertyId) {
      setCompetitors([])
      setPersonas([])
      setTopics([])
      setOriginalCompetitors([])
      setOriginalPersonas([])
      setOriginalTopics([])
      return
    }

    setListsLoading(true)
    try {
      const [competitorRes, personaRes, topicRes] = await Promise.all([
        api.get(`client-property/${propertyId}/competitors/`),
        api.get(`client-property/${propertyId}/personas/`),
        api.get(`client-property/${propertyId}/topics/`),
      ])
      const competitorItems = (competitorRes.data || []).map(toDraftItem)
      const personaItems = (personaRes.data || []).map(toDraftItem)
      const topicItems = (topicRes.data || []).map(toDraftItem)

      setCompetitors(competitorItems)
      setPersonas(personaItems)
      setTopics(topicItems)
      setOriginalCompetitors(competitorItems)
      setOriginalPersonas(personaItems)
      setOriginalTopics(topicItems)
    } catch {
      notify('Impossibile caricare le liste Relevia.', 'error')
    } finally {
      setListsLoading(false)
    }
  }

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true)
      try {
        const requests = [api.get('tagging-tool/reports/')]
        if (isWebranking) {
          requests.unshift(api.get('clienti/'))
        }

        const responses = await Promise.all(requests)
        if (isWebranking) {
          const clientiRes = responses[0]
          const taggingToolRes = responses[1]
          setClienti(
            Array.isArray(clientiRes.data) ? clientiRes.data : clientiRes.data?.results || []
          )
          setTaggingToolReports(taggingToolRes.data || [])
        } else {
          setTaggingToolReports(responses[0].data || [])
        }
        const loadedClientPropertyId = await loadDetails()
        if (loadedClientPropertyId) {
          await loadRelatedLists(loadedClientPropertyId)
        }
      } catch {
        notify('Errore nel caricamento della configurazione.', 'error')
      } finally {
        setInitialLoading(false)
      }
    }

    loadInitialData()
  }, [id, isWebranking])

  const updateVisitedStep = (stepIndex) => {
    setVisitedSteps((current) => (current.includes(stepIndex) ? current : [...current, stepIndex]))
  }

  const goToStep = (stepIndex) => {
    setActiveStep(stepIndex)
    updateVisitedStep(stepIndex)
  }

  const getListItems = (kind) =>
    listState[kind].items.map(normalizeListItem).filter((item) => item.name)

  const getStepStatus = (stepIndex) => {
    if (stepIndex === activeStep) {
      return 'active'
    }
    if (isStepComplete(stepIndex)) {
      return 'complete'
    }
    if (visitedSteps.includes(stepIndex)) {
      return 'visited'
    }
    return 'idle'
  }

  const isStepComplete = (stepIndex) => {
    const stepKey = steps[stepIndex].key
    if (stepKey === 'base') {
      return !!name && (!isWebranking || !!selectedClientId) && (isEdit || !!file)
    }
    if (stepKey === 'competitors' || stepKey === 'personas' || stepKey === 'topics') {
      return getListItems(stepKey).length > 0
    }
    if (stepKey === 'schedule') {
      return !!startDate && !!interval && Number(interval) >= 1
    }
    if (stepKey === 'llm') {
      return useGpt || useGemini || usePerplexity
    }
    if (stepKey === 'prompt') {
      return !!customPrompt && customPrompt.includes('{keyword}')
    }
    return false
  }

  const validateStep = (stepIndex) => {
    const stepKey = steps[stepIndex].key

    if (stepKey === 'base') {
      if (!name) {
        notify('Inserisci il nome della schedule.', 'error')
        return false
      }
      if (!derivedClientPropertySlug) {
        notify('Il nome della schedule non genera uno slug Relevia valido.', 'error')
        return false
      }
      if (isWebranking && !selectedClientId) {
        notify('Seleziona un cliente.', 'error')
        return false
      }
      if (!isEdit && !file) {
        notify('Carica un file Excel con le keyword.', 'error')
        return false
      }
    }

    if (stepKey === 'competitors' || stepKey === 'personas' || stepKey === 'topics') {
      if (getListItems(stepKey).length === 0) {
        notify(`Inserisci almeno un elemento in ${listConfigs[stepKey].pluralLabel}.`, 'error')
        return false
      }
    }

    if (stepKey === 'schedule' && (!interval || Number(interval) < 1)) {
      notify("L'intervallo deve essere almeno 1.", 'error')
      return false
    }

    if (stepKey === 'llm' && !useGpt && !useGemini && !usePerplexity) {
      notify('Seleziona almeno un modello LLM.', 'error')
      return false
    }

    if (stepKey === 'prompt' && customPrompt && !customPrompt.includes('{keyword}')) {
      notify('Il custom prompt deve contenere {keyword}.', 'error')
      return false
    }

    return true
  }

  const goNext = () => {
    if (!validateStep(activeStep)) {
      return
    }
    if (activeStep < steps.length - 1) {
      goToStep(activeStep + 1)
    }
  }

  const goBack = () => {
    if (activeStep > 0) {
      goToStep(activeStep - 1)
    }
  }

  const updateListItem = (kind, index, field, value) => {
    const { items, setItems } = listState[kind]
    const nextItems = [...items]
    const previousItem = nextItems[index]
    nextItems[index] = { ...previousItem, [field]: value }
    if (field === 'display_name') {
      const previousAutoSlug = slugifyName(previousItem.display_name)
      if (!previousItem.name || previousItem.name === previousAutoSlug) {
        nextItems[index].name = slugifyName(value)
      }
    }
    setItems(nextItems)
  }

  const addListItem = (kind) => {
    const { items, setItems } = listState[kind]
    setItems([...items, createDraftItem()])
  }

  const removeListItem = (kind, index) => {
    const { items, setItems } = listState[kind]
    setItems(items.filter((_, itemIndex) => itemIndex !== index))
  }

  const syncRelatedList = (kind) => {
    return getListItems(kind).map((item) => toPayload(kind, item))
  }

  const buildFormData = () => {
    const formData = new FormData()
    formData.append('name', name)
    if (selectedClientId) {
      formData.append('cliente_id', selectedClientId)
    }
    formData.append('brand', name)
    formData.append('client_domain', clientDomain.trim())
    formData.append('brand_aliases', JSON.stringify(normalizedBrandAliases))
    formData.append('competitors', JSON.stringify(syncRelatedList('competitors')))
    formData.append('personas', JSON.stringify(syncRelatedList('personas')))
    formData.append('topics', JSON.stringify(syncRelatedList('topics')))
    formData.append('country', country)
    formData.append('gpt', useGpt)
    formData.append('gemini', useGemini)
    formData.append('perplexity', usePerplexity)
    formData.append('use_dataforseo', useDataforseo)
    formData.append('custom_prompt', customPrompt)
    formData.append('start_date', startDate)
    formData.append('run_mode', runMode)
    formData.append('schedule_type', runMode === 'ONE_SHOT' ? 'M' : scheduleType)
    formData.append('interval', runMode === 'ONE_SHOT' ? 1 : Number(interval))
    formData.append('questions_only', questionsOnly)
    formData.append('active', active)
    if (taggingToolId) {
      formData.append('connected_tagging', taggingToolId)
    }
    if (file) {
      formData.append('file', file)
    }
    return formData
  }

  const buildEstimateFormData = () => {
    const formData = new FormData()
    if (file) {
      formData.append('file', file)
    }
    formData.append('gpt', useGpt)
    formData.append('gemini', useGemini)
    formData.append('perplexity', usePerplexity)
    formData.append('use_dataforseo', useDataforseo)
    return formData
  }

  const exportConfiguration = async () => {
    if (!isEdit) {
      return
    }

    try {
      const response = await api.get(`llm-visibility-schedules/${id}/export_configuration/`, {
        responseType: 'blob',
      })
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${derivedClientPropertySlug || 'relevia_configuration'}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      notify(err.response?.data?.error || 'Impossibile esportare la configurazione.', 'error')
    }
  }

  const estimateScheduleCost = async () => {
    const res = await api.post('llm-visibility/estimate_cost/', buildEstimateFormData())
    setEstimateParams(res.data)
    setShowEstimateCheck(true)
  }

  const submit = async (event, costEstimateAccepted = false, explicitSave = false) => {
    if (event) {
      event.preventDefault()
    }

    if (event && !costEstimateAccepted && !explicitSave) {
      if (activeStep < steps.length - 1) {
        goNext()
      }
      return
    }

    for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
      if (!validateStep(stepIndex)) {
        goToStep(stepIndex)
        return
      }
    }

    if (!questionsOnly && !costEstimateAccepted && (!isEdit || file)) {
      try {
        await estimateScheduleCost()
      } catch (err) {
        notify(err?.response?.data?.error || 'Impossibile calcolare la stima costi.', 'error')
      }
      return
    }

    setSaving(true)
    try {
      const savedSchedule = isEdit
        ? await upload('patch', `llm-visibility-schedules/${id}/`, buildFormData())
        : await upload('post', 'llm-visibility-schedules/', buildFormData())
      notify('Schedule salvata correttamente.', 'success')
      setEstimateAccepted(false)
      if (isEdit) {
        await loadDetails()
      } else if (savedSchedule?.id) {
        history.replace(`/add-relevia-configuration/${savedSchedule.id}`)
      }
    } catch (err) {
      notify(err?.response?.data?.error || 'Errore nel salvataggio della schedule.', 'error')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (estimateAccepted) {
      submit(null, true).finally(() => setEstimateAccepted(false))
    }
  }, [estimateAccepted])

  const confirmEstimate = (event) => {
    event.preventDefault()
    setShowEstimateCheck(false)
    setEstimateAccepted(true)
  }

  const cancelEstimate = () => {
    setShowEstimateCheck(false)
    setEstimateAccepted(false)
  }

  const executeScheduleAction = async (action) => {
    if (action === 'SAVE_CONFIG') {
      await submit(null, false, true)
      return
    }
    if (action === 'EXPORT_CONFIG') {
      await exportConfiguration()
      return
    }
    if (!isEdit || action === 'WAIT') {
      return
    }

    setActionLoading(action)
    try {
      await api.post(`llm-visibility-schedules/${id}/actions/${action}/`)
      notify(actionMessages[action] || 'Azione avviata correttamente.', 'success')
      await loadDetails()
    } catch (err) {
      notify(err.response?.data?.error || 'Impossibile eseguire questa azione.', 'error')
    } finally {
      setActionLoading('')
    }
  }

  const renderBaseStep = () => (
    <Row className="llm-wizard-base-step">
      <Col lg={7}>
        <FormGroup>
          <label htmlFor="schedule-v2-name" className="form-control-label">
            Nome schedule
          </label>
          <Input
            id="schedule-v2-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isEdit}
            required
          />
          <FormText>
            {isEdit
              ? 'Il nome della schedule non puo essere modificato dopo la creazione.'
              : 'Il nome inserito verra utilizzato anche per nominare il dataset su BigQuery.'}
          </FormText>
        </FormGroup>
        {isWebranking && (
          <ClienteField
            clienti={clienti}
            selectedClientId={selectedClientId}
            setSelectedClientId={setSelectedClientId}
            setBrand={() => {}}
            helpText="Seleziona il cliente. Il backend sincronizza il brand legacy e collega la schedule al cliente scelto."
          />
        )}
        <FormGroup>
          <label htmlFor="schedule-v2-client-domain" className="form-control-label">
            Client domain
          </label>
          <Input
            id="schedule-v2-client-domain"
            value={clientDomain}
            onChange={(e) => setClientDomain(e.target.value)}
            placeholder="example.com oppure https://www.example.com"
          />
          <FormText>Usato per riconoscere le brand citations nelle analisi Relevia.</FormText>
        </FormGroup>
        <FormGroup>
          <label htmlFor="schedule-v2-brand-aliases" className="form-control-label">
            Brand aliases
          </label>
          <Input
            id="schedule-v2-brand-aliases"
            type="textarea"
            rows="4"
            value={brandAliasesText}
            onChange={(e) => setBrandAliasesText(e.target.value)}
            placeholder="Un alias per riga oppure separati da virgola"
          />
          <FormText>
            Alias opzionali del brand usati nelle domande e nelle analisi Relevia.
          </FormText>
        </FormGroup>
      </Col>
      <Col lg={5} className="llm-wizard-file-column">
        <FileInput
          onFileChange={handleKeywordFileChange}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          helpText={
            <>
              {isEdit
                ? 'Carica un nuovo file solo se vuoi sostituire le keyword.'
                : 'File Excel con colonna Keyword.'}{' '}
              <a href={keywordTemplateUrl} download="llm_visibility_keyword_template.xlsx">
                Scarica file di esempio.
              </a>
            </>
          }
          required={!isEdit}
        />
        <FormGroup className="mt-3 mb-0">
          <label htmlFor="schedule-v2-country" className="form-control-label">
            Country
          </label>
          <Input
            id="schedule-v2-country"
            type="select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            <option value="">Non forzare la country</option>
            <option value="IT">IT</option>
            <option value="GB">GB</option>
            <option value="US">US</option>
            <option value="FR">FR</option>
            <option value="ES">ES</option>
          </Input>
          <FormText>La country viene applicata solo alle risposte GPT.</FormText>
        </FormGroup>
      </Col>
    </Row>
  )

  const renderListStep = (kind) => {
    const config = listConfigs[kind]
    const { items } = listState[kind]

    return (
      <div>
        <div className="llm-wizard-list-head">
          <div>
            <p>{config.description}</p>
          </div>
        </div>

        {listsLoading && isEdit && clientPropertyId && (
          <div className="llm-wizard-empty-state" role="status" aria-live="polite">
            Caricamento lista...
          </div>
        )}

        {!listsLoading && items.length === 0 && (
          <div className="llm-wizard-empty-state">
            Nessun elemento presente. Aggiungi il primo elemento per continuare.
          </div>
        )}

        {!listsLoading &&
          items.map((item, index) => (
            <div className="llm-wizard-list-item" key={item.id || `new-${index}`}>
              <Row>
                <Col md={5}>
                  <FormGroup>
                    <label className="form-control-label" htmlFor={`${kind}-display-${index}`}>
                      Nome visibile
                    </label>
                    <Input
                      id={`${kind}-display-${index}`}
                      value={item.display_name}
                      onChange={(e) => updateListItem(kind, index, 'display_name', e.target.value)}
                      placeholder={`${config.label} visibile`}
                    />
                  </FormGroup>
                </Col>
                <Col md={5}>
                  <FormGroup>
                    <label className="form-control-label" htmlFor={`${kind}-name-${index}`}>
                      Nome tecnico
                    </label>
                    <Input
                      id={`${kind}-name-${index}`}
                      value={item.name}
                      onChange={(e) => updateListItem(kind, index, 'name', e.target.value)}
                      placeholder="slug-tecnico"
                    />
                  </FormGroup>
                </Col>
                <Col md={2} className="llm-wizard-list-remove">
                  <Button
                    color="secondary"
                    type="button"
                    onClick={() => removeListItem(kind, index)}
                  >
                    Rimuovi
                  </Button>
                </Col>
              </Row>
              {config.hasSynonyms && (
                <FormGroup>
                  <label className="form-control-label" htmlFor={`${kind}-synonyms-${index}`}>
                    Sinonimi
                  </label>
                  <Input
                    id={`${kind}-synonyms-${index}`}
                    value={item.synonymsText}
                    onChange={(e) => updateListItem(kind, index, 'synonymsText', e.target.value)}
                    placeholder="Uno o piu sinonimi separati da virgola"
                  />
                </FormGroup>
              )}
              {config.hasDescription && (
                <FormGroup>
                  <label className="form-control-label" htmlFor={`${kind}-description-${index}`}>
                    Descrizione
                  </label>
                  <Input
                    id={`${kind}-description-${index}`}
                    type="textarea"
                    rows="3"
                    value={item.description}
                    onChange={(e) => updateListItem(kind, index, 'description', e.target.value)}
                    placeholder={`Descrivi ${config.label.toLowerCase()}`}
                  />
                </FormGroup>
              )}
            </div>
          ))}
        <div className="llm-wizard-list-footer">
          <Button color="info" type="button" onClick={() => addListItem(kind)}>
            + Aggiungi
          </Button>
        </div>
      </div>
    )
  }

  const renderScheduleStep = () => (
    <Row>
      <Col md={4}>
        <FormGroup>
          <label htmlFor="schedule-v2-start-date" className="form-control-label">
            Start date
          </label>
          <Input
            id="schedule-v2-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          <label htmlFor="schedule-v2-run-mode" className="form-control-label">
            Run mode
          </label>
          <Input
            id="schedule-v2-run-mode"
            type="select"
            value={runMode}
            onChange={(e) => setRunMode(e.target.value)}
          >
            <option value="RECURRING">Recurring</option>
            <option value="ONE_SHOT">One-shot</option>
          </Input>
          <FormText>One-shot esegue il primo export e poi si ferma.</FormText>
        </FormGroup>
      </Col>
      {runMode !== 'ONE_SHOT' && (
        <>
          <Col md={2}>
            <FormGroup>
              <label htmlFor="schedule-v2-type" className="form-control-label">
                Schedule type
              </label>
              <Input
                id="schedule-v2-type"
                type="select"
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value)}
              >
                <option value="M">Monthly</option>
                <option value="W">Weekly</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup>
              <label htmlFor="schedule-v2-interval" className="form-control-label">
                Interval
              </label>
              <Input
                id="schedule-v2-interval"
                type="number"
                min="1"
                step="1"
                value={interval}
                onChange={(e) => setIntervalValue(e.target.value)}
                required
              />
            </FormGroup>
          </Col>
        </>
      )}
    </Row>
  )
  const renderLlmStep = () => (
    <Row className="align-items-start">
      <Col md={6} className="mb-3 mb-md-0">
        <FormGroup>
          <label className="form-control-label">LLM da usare</label>
          <div className="llm-wizard-check-grid">
            <div className="llm-wizard-check-item">
              <input
                id="wizard-gpt"
                type="checkbox"
                checked={useGpt}
                onChange={() => setUseGpt(!useGpt)}
              />
              <label htmlFor="wizard-gpt">OpenAI GPT</label>
            </div>
            <div className="llm-wizard-check-item">
              <input
                id="wizard-gemini"
                type="checkbox"
                checked={useGemini}
                onChange={() => setUseGemini(!useGemini)}
              />
              <label htmlFor="wizard-gemini">Google Gemini</label>
            </div>
            <div className="llm-wizard-check-item">
              <input
                id="wizard-perplexity"
                type="checkbox"
                checked={usePerplexity}
                onChange={() => setUsePerplexity(!usePerplexity)}
              />
              <label htmlFor="wizard-perplexity">Perplexity</label>
            </div>
          </div>
          {(useGpt || useGemini) && (
            <div className="mt-3">
              <label className="form-control-label d-block mb-2">
                Modalità di esecuzione (GPT e Gemini)
              </label>
              <div className="custom-control custom-radio custom-control-inline">
                <input
                  className="custom-control-input"
                  id="wizard-llm-direct"
                  name="wizard-llm-mode"
                  type="radio"
                  checked={!useDataforseo}
                  onChange={() => setUseDataforseo(false)}
                />
                <label className="custom-control-label" htmlFor="wizard-llm-direct">
                  API diretta
                </label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <input
                  className="custom-control-input"
                  id="wizard-llm-dataforseo"
                  name="wizard-llm-mode"
                  type="radio"
                  checked={useDataforseo}
                  onChange={() => setUseDataforseo(true)}
                />
                <label className="custom-control-label" htmlFor="wizard-llm-dataforseo">
                  DataForSEO Scraper
                </label>
              </div>
            </div>
          )}
          <FormText>Seleziona almeno un modello.</FormText>
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <label htmlFor="schedule-v2-tagging-tool" className="form-control-label">
            Connetti Tagging Tool
          </label>
          <Select
            inputId="schedule-v2-tagging-tool"
            name="schedule-v2-tagging-tool"
            aria-label="Connetti Tagging Tool"
            options={taggingToolReports.map((report) => ({ value: report.id, label: report.name }))}
            isClearable
            onChange={(option) => setTaggingToolId(option?.value || '')}
            value={
              taggingToolId
                ? {
                    value: taggingToolId,
                    label: taggingToolReports.find((report) => report.id === taggingToolId)?.name,
                  }
                : null
            }
          />
          <FormText>Collegando un report esistente puoi riusare i tag gia disponibili.</FormText>
        </FormGroup>
      </Col>
    </Row>
  )

  const renderPromptStep = () => (
    <div>
      <FormGroup>
        <label htmlFor="schedule-v2-custom-prompt" className="form-control-label">
          Custom prompt
        </label>
        <Input
          id="schedule-v2-custom-prompt"
          type="textarea"
          rows="12"
          className="llm-wizard-prompt"
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
        />
        <FormText>Deve contenere {'{keyword}'}.</FormText>
      </FormGroup>
      <Row>
        <Col md={6} className="mb-3 mb-md-0">
          <FormGroup className="llm-wizard-toggle">
            <div className="llm-wizard-toggle-head">
              <label className="form-control-label mb-0" htmlFor="schedule-v2-questions-only">
                Questions only
              </label>
              <input
                id="schedule-v2-questions-only"
                type="checkbox"
                checked={questionsOnly}
                onChange={() => setQuestionsOnly(!questionsOnly)}
              />
            </div>
            <FormText>
              Se attivo, verranno generate solo le domande e il run delle risposte non partira
              finche non viene attivato manualmente.
            </FormText>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup className="llm-wizard-toggle">
            <div className="llm-wizard-toggle-head">
              <label className="form-control-label mb-0" htmlFor="schedule-v2-active">
                Active
              </label>
              <input
                id="schedule-v2-active"
                type="checkbox"
                checked={active}
                onChange={() => setActive(!active)}
              />
            </div>
            <FormText>Disattiva la schedule per metterla in pausa senza eliminarla.</FormText>
          </FormGroup>
        </Col>
      </Row>
      {isEdit &&
        questionsOnly &&
        (hasAction(availableActions, 'GENERATE_QUESTIONS') ||
          hasAction(availableActions, 'REGENERATE_QUESTIONS')) && (
          <div className="alert alert-info mt-3">
            Questions mode attivo. Questions generated: <b>{questionsGenerated ? 'Yes' : 'No'}</b>.
          </div>
        )}
    </div>
  )

  const renderCurrentStep = () => {
    const stepKey = steps[activeStep].key
    if (stepKey === 'base') return renderBaseStep()
    if (stepKey === 'competitors' || stepKey === 'personas' || stepKey === 'topics')
      return renderListStep(stepKey)
    if (stepKey === 'schedule') return renderScheduleStep()
    if (stepKey === 'llm') return renderLlmStep()
    return renderPromptStep()
  }

  const renderAvailableActions = () => {
    const actionsToShow = isEdit
      ? Array.from(new Set([...availableActions, 'EXPORT_CONFIG']))
      : ['SAVE_CONFIG']

    return (
      <div className="llm-wizard-available-actions">
        <span>Azioni disponibili</span>
        {actionsToShow.length > 0 ? (
          <div className="llm-wizard-available-actions__buttons">
            {actionsToShow.map((action) => {
              const isSaveAction = action === 'SAVE_CONFIG'
              const isWaitingAction = action === 'WAIT'
              const isCurrentActionLoading = actionLoading === action
              const isActionDisabled =
                isWaitingAction || saving || (!!actionLoading && !isCurrentActionLoading)
              const tooltipId = `llm-wizard-action-${action.toLowerCase().replace(/_/g, '-')}`
              return (
                <div className="llm-wizard-available-actions__button" key={action} id={tooltipId}>
                  <Button
                    color={actionButtonColors[action] || 'secondary'}
                    className={actionButtonClasses[action] || ''}
                    type="button"
                    size="sm"
                    block
                    disabled={isActionDisabled}
                    onClick={() => executeScheduleAction(action)}
                  >
                    {isSaveAction && saving
                      ? 'Salvataggio...'
                      : isCurrentActionLoading
                      ? 'Esecuzione...'
                      : actionLabels[action] || action}
                  </Button>
                  <UncontrolledTooltip target={tooltipId} placement="right">
                    {actionTooltips[action] || 'Esegue l azione selezionata.'}
                  </UncontrolledTooltip>
                </div>
              )
            })}
          </div>
        ) : (
          <p>{isEdit ? 'Nessuna azione disponibile.' : 'Disponibili dopo il salvataggio.'}</p>
        )}
      </div>
    )
  }

  const renderSummary = () => (
    <div className="llm-wizard-summary">
      <div>
        <span>ClientProperty</span>
        <strong>{derivedClientPropertyName || 'Non impostato'}</strong>
      </div>
      <div>
        <span>Dataset slug</span>
        <strong>{derivedClientPropertySlug || 'Non disponibile'}</strong>
      </div>
      <div>
        <span>Liste</span>
        <strong>
          {getListItems('competitors').length} competitor, {getListItems('personas').length}{' '}
          personas, {getListItems('topics').length} topic
        </strong>
      </div>
      <div>
        <span>LLM</span>
        <strong>
          {[useGpt && 'GPT', useGemini && 'Gemini', usePerplexity && 'Perplexity']
            .filter(Boolean)
            .join(', ') || 'Non selezionati'}
        </strong>
      </div>
    </div>
  )

  return (
    <Header
      iconClass="far fa-calendar-alt text-white"
      title="Relevia Setup"
      description="Wizard guidato per configurare schedule, contesto Relevia e opzioni di esecuzione."
    >
      <div className="pb-5">
        <Card className="bg-secondary shadow llm-wizard-card">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col md={9}>
                <h1 className="mb-0">{isEdit ? 'Edit Schedule' : 'Nuova Schedule'}</h1>
              </Col>
              <Col md={3} className="text-md-right mt-3 mt-md-0">
                <Button
                  color="primary"
                  type="button"
                  onClick={() => history.push('/relevia-setup')}
                >
                  &larr; Indietro
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <ScheduleEstimateModal
              show={showEstimateCheck}
              onHide={cancelEstimate}
              onSubmit={confirmEstimate}
              params={estimateParams}
              scheduleType={scheduleType}
              interval={interval}
            />
            {initialLoading || uploadLoading ? (
              <div className="text-center" role="status" aria-live="polite">
                <Spinner animation="border" role="status" />
                <span className="sr-only">Caricamento schedule Relevia...</span>
              </div>
            ) : (
              <Form onSubmit={submit}>
                <div className="llm-wizard-layout">
                  <aside className="llm-wizard-sidebar">
                    <nav className="llm-wizard-steps" aria-label="Step configurazione schedule">
                      {steps.map((step, index) => (
                        <button
                          className={`llm-wizard-step llm-wizard-step--${getStepStatus(index)}`}
                          type="button"
                          key={step.key}
                          onClick={() => goToStep(index)}
                        >
                          <span>{index + 1}</span>
                          <strong>{step.label}</strong>
                        </button>
                      ))}
                    </nav>
                    {renderAvailableActions()}
                  </aside>
                  <section className="llm-wizard-panel">
                    {renderSummary()}
                    <div className="llm-wizard-panel-head">
                      <span>
                        Step {activeStep + 1} di {steps.length}
                      </span>
                      <h2>{steps[activeStep].label}</h2>
                    </div>
                    {renderCurrentStep()}
                  </section>
                </div>

                <Row className="mt-4 mb-3 llm-wizard-actions">
                  <Col md={12} className="text-md-right">
                    <Button
                      color="secondary"
                      type="button"
                      className="mr-2 mb-2"
                      onClick={goBack}
                      disabled={activeStep === 0}
                    >
                      Indietro
                    </Button>
                    {activeStep < steps.length - 1 && (
                      <Button color="info" type="button" className="mb-2" onClick={goNext}>
                        Avanti
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            )}
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </Header>
  )
}

export { normalizeBrandAliases, parseRelatedListsFromWorkbook, toBigQuerySlug }
export default LLMVisibilityScheduledWizardForm
